import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useCart } from '@/hooks/useCart';
import { useAuth } from '@/hooks/useAuth';
import { getProductImage } from '@/lib/productMedia';

// ============================================================
// Chat Live (site public du marchand).
// Une bulle de discussion permet aux visiteurs de :
//   • poser des questions sur la boutique (prix, produits, contact) ;
//   • découvrir des produits recommandés par l'IA ;
//   • ouvrir une fiche produit (modal) et l'ajouter au panier ;
//   • obtenir un récapitulatif du panier avec un bouton « Commander ».
//
// PERSISTANCE : chaque échange est enregistré côté serveur (Edge Function
// `chat-live`) et rattaché à un identifiant de visiteur stable stocké dans
// le navigateur. La conversation survit donc au rafraîchissement et à la
// navigation. L'adresse IP du visiteur est aussi enregistrée ; si le
// visiteur se connecte/ouvre un compte plus tard, ses conversations sont
// automatiquement rattachées à son compte.
// ============================================================

interface ChatConfig {
  enabled: boolean;
  agent_name: string;
  welcome_message: string;
  primary_color: string;
  show_products: boolean;
}

interface ChatMsg {
  role: 'user' | 'assistant';
  content: string;
  productIds?: number[];
  streaming?: boolean;
}

interface ChatProduct {
  id: number;
  name: string;
  description: string | null;
  price: number;
  discount_enabled: boolean;
  discount_price: number | null;
  stock: number;
  currency: string;
  media: { url: string; type: string }[] | null;
}

export default function ChatLiveWidget() {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { count, subtotal, currency, addToCart } = useCart();
  const navigate = useNavigate();

  const [config, setConfig] = useState<ChatConfig | null>(null);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsById, setProductsById] = useState<Record<number, ChatProduct>>({});
  const [modalProduct, setModalProduct] = useState<ChatProduct | null>(null);
  const [modalQty, setModalQty] = useState(1);
  const [addedNotice, setAddedNotice] = useState(false);
  const [visitorId, setVisitorId] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const claimedUserRef = useRef<number | null>(null);

  const boutiqueId = tenant?.id ?? null;
  const visitorUserId = user?.id ?? null;

  // Identifiant de visiteur stable (survit au refresh et à la navigation).
  useEffect(() => {
    if (!boutiqueId) return;
    const key = `zifek_live_visitor_${boutiqueId}`;
    let value = '';
    try { value = localStorage.getItem(key) || ''; } catch { value = ''; }
    if (!value) {
      value = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      try { localStorage.setItem(key, value); } catch { /* stockage indisponible */ }
    }
    setVisitorId(value);
  }, [boutiqueId]);

  // Identifiant de conversation EN COURS. Il vaut par défaut l'identifiant du
  // visiteur (compatibilité avec les conversations déjà enregistrées) et peut
  // être renouvelé via le bouton « Nouvelle conversation » : le visiteur
  // repart alors d'un fil vide tout en restant identifié comme le même
  // visiteur côté marchand.
  useEffect(() => {
    if (!boutiqueId || !visitorId) return;
    const key = `zifek_live_session_${boutiqueId}`;
    let value = '';
    try { value = localStorage.getItem(key) || ''; } catch { value = ''; }
    setSessionId(value || visitorId);
  }, [boutiqueId, visitorId]);

  // Charge la configuration du chat-live du marchand
  useEffect(() => {
    if (!boutiqueId) return;
    let cancelled = false;
    supabase
      .from('chat_live_config')
      .select('enabled, agent_name, welcome_message, primary_color, show_products')
      .eq('idcommerce', boutiqueId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data) setConfig(data as ChatConfig);
      });
    return () => { cancelled = true; };
  }, [boutiqueId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const ensureProducts = useCallback(async (ids: number[]) => {
    const missing = ids.filter((id) => !productsById[id]);
    if (missing.length === 0) return;
    setLoadingProducts(true);
    try {
      const { data } = await supabase
        .from('product_items')
        .select('id, name, description, price, discount_enabled, discount_price, stock, currency, media')
        .in('id', missing);
      if (data) {
        setProductsById((prev) => {
          const next = { ...prev };
          (data as ChatProduct[]).forEach((p) => { next[p.id] = p; });
          return next;
        });
      }
    } finally {
      setLoadingProducts(false);
    }
  }, [productsById]);

  const ensureProductsRef = useRef(ensureProducts);
  useEffect(() => { ensureProductsRef.current = ensureProducts; }, [ensureProducts]);

  // Recharge l'historique persistant de la conversation du visiteur.
  const loadHistory = useCallback(async () => {
    if (!boutiqueId || !sessionId) return;
    try {
      const { data } = await supabase
        .from('chat_live_conversations')
        .select('role, content, product_ids, created_at')
        .eq('idcommerce', boutiqueId)
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true })
        .limit(300);
      const rows = (data || []) as Array<{ role: string; content: string | null; product_ids: unknown }>;
      if (rows.length > 0) {
        const msgs: ChatMsg[] = rows.map((r) => ({
          role: r.role === 'user' ? 'user' : 'assistant',
          content: r.content || '',
          productIds: Array.isArray(r.product_ids)
            ? (r.product_ids as number[]).map(Number).filter((n) => Number.isFinite(n) && n > 0)
            : [],
        }));
        setMessages(msgs);
        const ids = Array.from(new Set(msgs.flatMap((m) => m.productIds || [])));
        if (ids.length > 0) void ensureProductsRef.current(ids);
      }
    } catch { /* silencieux : on repart de l'accueil */ }
  }, [boutiqueId, sessionId]);

  // Charge l'historique dès que la config et l'identité sont prêtes.
  useEffect(() => {
    if (!config?.enabled || !boutiqueId || !sessionId) return;
    let cancelled = false;
    setHistoryLoaded(false);
    (async () => {
      await loadHistory();
      if (!cancelled) setHistoryLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [config?.enabled, boutiqueId, sessionId, loadHistory]);

  // Message d'accueil uniquement quand la conversation est vide.
  useEffect(() => {
    if (config?.enabled && historyLoaded && messages.length === 0) {
      setMessages([{ role: 'assistant', content: config.welcome_message || 'Bonjour ! Comment puis-je vous aider ?' }]);
    }
  }, [config, historyLoaded, messages.length]);

  // Quand le visiteur se connecte, on rattache ses conversations anonymes
  // (repérées par son identifiant de visiteur ou son IP) à son compte.
  useEffect(() => {
    if (!boutiqueId || !visitorId || !visitorUserId) return;
    if (claimedUserRef.current === visitorUserId) return;
    claimedUserRef.current = visitorUserId;
    supabase.functions
      .invoke('chat-live', { body: { boutiqueId, mode: 'claim', visitorId, visitorUserId } })
      .then(() => { void loadHistory(); })
      .catch(() => { /* silencieux */ });
  }, [boutiqueId, visitorId, visitorUserId, loadHistory]);

  const setLastAssistant = useCallback((patch: Partial<ChatMsg>) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const copy = [...prev];
      const last = copy.length - 1;
      if (copy[last].role !== 'assistant') return prev;
      copy[last] = { ...copy[last], ...patch };
      return copy;
    });
  }, []);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || sending || !boutiqueId || !visitorId || !sessionId) return;

    const historyForApi: ChatMsg[] = [...messages.filter((m) => m.content), { role: 'user', content }];
    setMessages([...historyForApi, { role: 'assistant', content: '', streaming: true }]);
    setInput('');
    setSending(true);

    const requestBody = {
      boutiqueId,
      agentName: config?.agent_name || 'Assistant',
      sessionId,
      visitorId,
      visitorUserId: visitorUserId || null,
      page: typeof window !== 'undefined' ? window.location.pathname : '',
      messages: historyForApi.map((m) => ({ role: m.role, content: m.content })),
      stream: true,
    };

    // 1) Tentative en streaming (réponse affichée au fil de l'eau = plus rapide à lire).
    try {
      const base = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string) || '';
      const anon = (import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string) || '';
      if (base && typeof fetch !== 'undefined') {
        const res = await fetch(`${base}/functions/v1/chat-live`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: anon, Authorization: `Bearer ${anon}` },
          body: JSON.stringify(requestBody),
        });
        if (res.ok && res.body) {
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let acc = '';
          let finished = false;
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';
            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed.startsWith('data:')) continue;
              const ds = trimmed.slice(5).trim();
              if (!ds || ds === '[DONE]') continue;
              let evt: { delta?: string; done?: boolean; full?: string; productIds?: number[]; error?: string } | null = null;
              try { evt = JSON.parse(ds); } catch { continue; }
              if (!evt) continue;
              if (evt.error) throw new Error(evt.error);
              if (typeof evt.delta === 'string' && evt.delta) {
                acc += evt.delta;
                setLastAssistant({ content: acc });
              }
              if (evt.done) {
                finished = true;
                const productIds = config?.show_products ? (evt.productIds || []) : [];
                if (productIds.length > 0) void ensureProductsRef.current(productIds);
                setLastAssistant({ content: evt.full || acc || "Je n'ai pas pu répondre.", productIds, streaming: false });
              }
            }
          }
          if (!finished) setLastAssistant({ content: acc || "Je n'ai pas pu répondre.", streaming: false });
          setSending(false);
          return;
        }
      }
    } catch {
      // On bascule sur le repli classique ci-dessous.
    }

    // 2) Repli non-streaming.
    try {
      const { data, error } = await supabase.functions.invoke('chat-live', {
        body: { ...requestBody, stream: false },
      });
      if (error) throw error;
      const payload = data as { reply?: string; productIds?: number[] } | null;
      const productIds = config?.show_products ? (payload?.productIds || []) : [];
      if (productIds.length > 0) void ensureProductsRef.current(productIds);
      setLastAssistant({ content: payload?.reply || "Je n'ai pas pu répondre.", productIds, streaming: false });
    } catch {
      setLastAssistant({ content: "Désolé, une erreur est survenue. Réessayez dans un instant.", streaming: false });
    } finally {
      setSending(false);
    }
  };

  const openProduct = (p: ChatProduct) => {
    setModalProduct(p);
    setModalQty(1);
  };

  // Repart de zéro : nouveau fil de conversation vide pour le visiteur.
  const startNewConversation = () => {
    if (!boutiqueId || sending) return;
    const fresh = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    try { localStorage.setItem(`zifek_live_session_${boutiqueId}`, fresh); } catch { /* stockage indisponible */ }
    setSessionId(fresh);
    setProductsById({});
    setModalProduct(null);
    setInput('');
    setMessages([{ role: 'assistant', content: config?.welcome_message || 'Bonjour ! Comment puis-je vous aider ?' }]);
  };

  const handleAdd = (p: ChatProduct, qty = 1) => {
    const price = p.discount_enabled && p.discount_price != null ? Number(p.discount_price) : Number(p.price);
    addToCart({
      id: p.id,
      name: p.name,
      price,
      original_price: Number(p.price),
      media: getProductImage(p.media) || '',
      stock: p.stock ?? 0,
      product_type: 'physical',
    }, qty);
    setAddedNotice(true);
    setTimeout(() => setAddedNotice(false), 2500);
    setModalProduct(null);
  };

  if (!config?.enabled || !boutiqueId) return null;

  const color = config.primary_color || '#1f2937';
  const modalMax = modalProduct && modalProduct.stock > 0 ? modalProduct.stock : 1;

  return (
    <>
      {/* Bulle flottante */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          style={{ backgroundColor: color }}
          className="fixed bottom-5 right-5 z-[80] w-14 h-14 rounded-full text-white flex items-center justify-center shadow-lg hover:scale-105 transition-transform cursor-pointer"
          aria-label="Ouvrir le chat"
        >
          <i className="ri-customer-service-2-line text-2xl"></i>
          {count > 0 && (
            <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-accent-500 text-white text-[10px] font-bold flex items-center justify-center">{count}</span>
          )}
        </button>
      )}

      {/* Panneau de chat */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[80] w-[92vw] max-w-[380px] h-[560px] max-h-[80vh] bg-background-50 rounded-2xl border border-background-200/70 flex flex-col overflow-hidden animate-scale-in">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 text-white flex-shrink-0" style={{ backgroundColor: color }}>
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <i className="ri-robot-2-line"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{config.agent_name || 'Assistant'}</p>
              <p className="text-[11px] text-white/70 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-300"></span> En ligne
              </p>
            </div>
            <button
              onClick={startNewConversation}
              disabled={sending}
              title="Nouvelle conversation"
              aria-label="Nouvelle conversation"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <i className="ri-refresh-line"></i>
            </button>
            <button onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-background-100/40">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className="max-w-[85%]">
                  <div className={`px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    m.role === 'user' ? 'text-white rounded-br-sm' : 'bg-background-50 border border-background-200/70 text-foreground-800 rounded-bl-sm'
                  }`} style={m.role === 'user' ? { backgroundColor: color } : undefined}>
                    {m.role === 'assistant' && m.streaming && !m.content ? (
                      <span className="flex items-center gap-1 py-0.5">
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                      </span>
                    ) : (
                      <span className="whitespace-pre-wrap">{m.content}</span>
                    )}
                  </div>
                  {m.productIds && m.productIds.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {m.productIds.map((id) => {
                        const p = productsById[id];
                        if (!p) return null;
                        const price = p.discount_enabled && p.discount_price != null ? Number(p.discount_price) : Number(p.price);
                        const img = getProductImage(p.media);
                        return (
                          <button
                            key={id}
                            onClick={() => openProduct(p)}
                            className="w-full flex items-center gap-2 p-2 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-300 transition-colors cursor-pointer text-left"
                          >
                            <div className="w-11 h-11 rounded-md overflow-hidden bg-background-100 flex-shrink-0">
                              {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-image-line"></i></div>}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-foreground-900 truncate">{p.name}</p>
                              <p className="text-xs font-bold text-primary-600">{price.toLocaleString()} {p.currency || currency}</p>
                            </div>
                            <i className="ri-arrow-right-s-line text-foreground-400"></i>
                          </button>
                        );
                      })}
                      {loadingProducts && <div className="text-center"><i className="ri-loader-4-line animate-spin text-primary-500"></i></div>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {addedNotice && (
              <div className="sticky bottom-0 flex items-center gap-2 px-3 py-2 rounded-lg bg-accent-50 text-accent-700 text-xs font-medium">
                <i className="ri-checkbox-circle-line"></i> Produit ajouté au panier
              </div>
            )}
          </div>

          {/* Panier recap */}
          {count > 0 && (
            <div className="px-3 py-2 border-t border-background-200/70 bg-background-50 flex items-center justify-between gap-2 flex-shrink-0">
              <button onClick={() => void send('Peux-tu me faire un récapitulatif de mon panier ?')} className="flex items-center gap-2 text-xs text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer">
                <i className="ri-shopping-cart-line text-primary-500"></i>
                <span><b>{count}</b> article{count > 1 ? 's' : ''} · {subtotal.toLocaleString()} {currency}</span>
              </button>
              <button onClick={() => navigate('/checkout')} className="px-3 py-1.5 rounded-full text-xs font-semibold text-white whitespace-nowrap cursor-pointer" style={{ backgroundColor: color }}>
                Commander
              </button>
            </div>
          )}

          {/* Input */}
          <div className="flex items-center gap-2 px-3 py-2.5 border-t border-background-200/70 flex-shrink-0">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void send(input); }}
              placeholder="Écrivez votre message..."
              className="flex-1 px-3 py-2 bg-background-100 rounded-full text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-1 focus:ring-primary-300"
            />
            <button
              onClick={() => void send(input)}
              disabled={sending || !input.trim()}
              style={{ backgroundColor: color }}
              className="w-9 h-9 rounded-full text-white flex items-center justify-center disabled:opacity-50 cursor-pointer flex-shrink-0"
            >
              <i className="ri-send-plane-fill text-sm"></i>
            </button>
          </div>
        </div>
      )}

      {/* Modal détail produit */}
      {modalProduct && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setModalProduct(null)}></div>
          <div className="relative bg-background-50 rounded-xl w-full max-w-sm overflow-hidden animate-scale-in">
            <div className="h-44 bg-background-100 relative">
              {getProductImage(modalProduct.media) ? (
                <img src={getProductImage(modalProduct.media)!} alt={modalProduct.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-image-line text-3xl"></i></div>
              )}
              <button onClick={() => setModalProduct(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center cursor-pointer hover:bg-black/60">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="p-4">
              <h4 className="text-base font-bold font-heading text-foreground-950">{modalProduct.name}</h4>
              <p className="mt-1 text-lg font-bold text-primary-600">
                {(modalProduct.discount_enabled && modalProduct.discount_price != null ? Number(modalProduct.discount_price) : Number(modalProduct.price)).toLocaleString()} {modalProduct.currency || currency}
                {modalProduct.discount_enabled && modalProduct.discount_price != null && (
                  <span className="ml-2 text-xs text-foreground-400 line-through">{Number(modalProduct.price).toLocaleString()}</span>
                )}
              </p>
              {modalProduct.description && (
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-4">{modalProduct.description}</p>
              )}
              <p className="mt-2 text-xs text-foreground-400">
                <i className="ri-archive-line mr-1"></i>
                {modalProduct.stock > 0 ? `En stock (${modalProduct.stock})` : 'Rupture de stock'}
              </p>

              {/* Sélecteur de quantité */}
              {modalProduct.stock > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-700">Quantité</span>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                    <button
                      onClick={() => setModalQty((q) => Math.max(1, q - 1))}
                      disabled={modalQty <= 1}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 text-foreground-700 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                      aria-label="Diminuer la quantité"
                    >
                      <i className="ri-subtract-line text-sm"></i>
                    </button>
                    <span className="w-9 text-center text-sm font-semibold text-foreground-900">{modalQty}</span>
                    <button
                      onClick={() => setModalQty((q) => Math.min(modalMax, q + 1))}
                      disabled={modalQty >= modalMax}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 text-foreground-700 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                      aria-label="Augmenter la quantité"
                    >
                      <i className="ri-add-line text-sm"></i>
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-4 flex items-center gap-2">
                <button
                  onClick={() => handleAdd(modalProduct, modalQty)}
                  disabled={modalProduct.stock <= 0}
                  style={modalProduct.stock > 0 ? { backgroundColor: color } : undefined}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-medium text-white disabled:bg-background-200 disabled:cursor-not-allowed cursor-pointer transition-colors"
                >
                  <i className="ri-shopping-cart-line"></i>
                  Ajouter {modalProduct.stock > 0 ? `(${modalQty})` : ''}
                </button>
                <button
                  onClick={() => { setModalProduct(null); navigate(`/product/${modalProduct.id}`); }}
                  className="px-4 py-2.5 rounded-full text-sm font-medium bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}